import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, navigate } from "@reach/router";
import Api from "Api/Api";
import CommWidget from "components/common/CommWidget";
import {
  startupStatus,
  startupStatusMap,
  reverseStartupStatusMap,
} from "formFields";
import DocumentBox from "components/common/DocumentBox";
import { AddAmountForm } from "components/forms/AddAmountForm";
import { AntTable } from "components/AntComponents";
import { Select, Checkbox } from "antd";
import Utils from "Utils";
import dayjs from "dayjs";
import { CSVLink } from "react-csv";

const SORT_FIELD_MAP = {
  name: "name",
  fund_name: "fund_name",
  whose_contact: "whose_contact",
  relationship_manager: "relationship_manager",
  status: "status",
  assigned: "assigned",
};

export default function Tracker() {
  const location = useLocation();
  const dispatch = useDispatch();

  const [startupID, setStartupID] = useState(null);
  const [startupName, setStartupName] = useState(null);
  const [data, setData] = useState([]);
  const [pageData, setPageData] = useState([]);
  const [showAmountPopup, setshowAmountPopup] = useState(false);
  const [committingInvestor, setCommittingInvestor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    columnKey: null,
    order: null,
  });
  const [selectedTag, setSelectedTag] = useState(null);
  const [hotLeadFilter, setHotLeadFilter] = useState(false);

  const formFields = useSelector((state) => state.formFields);

  const startupStatusDataSource = Utils.convertToAntSelectDatasource(startupStatus);

  const getStartupName = async (startupId) => {
    const startupResponse = await Api.getStartupName(startupId);
    if (startupResponse.status === 200) {
      setStartupName(startupResponse.data);
    }
  };

  const getPageSizeFromUrl = (url) => {
    if (!url) {
      return null;
    }
    try {
      const pageSizeFromUrl = new URL(url).searchParams.get("page_size");
      return pageSizeFromUrl ? Number(pageSizeFromUrl) : null;
    } catch (e) {
      return null;
    }
  };

  const getInvestorsData = useCallback(
    async (startupId, page = 1, search = "", sortValue = null, tag = null, hotLead = false) => {
      if (!startupId) {
        return;
      }
      setLoading(true);
      const filters = {};
      if (sortValue) filters.sort = sortValue;
      if (tag) filters.tags = tag;
      if (hotLead) filters.hot_lead = hotLead;
      const investorResponse = await Api.getInvestorsDataFromStartupID(
        startupId,
        null,
        page,
        search,
        filters
      );
      setLoading(false);
      if (investorResponse.status === 200) {
        const isPaginatedResponse = !Array.isArray(investorResponse.data);
        const resultData = isPaginatedResponse
          ? investorResponse.data.results || []
          : investorResponse.data || [];
        setData(resultData);
        setPageData(resultData);
        if (isPaginatedResponse) {
          setCurrentPage(page);
          setTotalRecords(investorResponse.data.count || 0);
          const apiPageSize =
            getPageSizeFromUrl(investorResponse.data.next) ||
            getPageSizeFromUrl(investorResponse.data.previous);
          if (apiPageSize) {
            setPageSize(apiPageSize);
          } else if (page === 1 && resultData.length > 0) {
            setPageSize(resultData.length);
          }
        } else {
          setCurrentPage(1);
          setTotalRecords(resultData.length);
          setPageSize(resultData.length || 10);
        }
      }
    },
    []
  );

  const handleSearch = (term) => {
    if (!startupID) {
      return;
    }
    setSearchTerm(term);
  };

  const handleSearchClear = () => {
    if (!startupID) {
      return;
    }
    setSearchTerm("");
  };

  useEffect(() => {
    Utils.getFormFields();
  }, []);

  useEffect(() => {
    dispatch({
      type: "SectionSelected",
      payload: "Tracker",
    });
    var startupId = location.pathname.split("/").splice(-1)[0];
    setStartupID(startupId);
    getStartupName(startupId);
  }, []);

  const sortParam = useMemo(() => {
    if (!sortConfig.columnKey || !sortConfig.order) {
      return null;
    }
    const field = SORT_FIELD_MAP[sortConfig.columnKey];
    return field ? `${field}|${sortConfig.order}` : null;
  }, [sortConfig]);

  useEffect(() => {
    if (!startupID) {
      return;
    }
    getInvestorsData(startupID, 1, searchTerm, sortParam, selectedTag, hotLeadFilter);
  }, [startupID, searchTerm, sortParam, selectedTag, hotLeadFilter, getInvestorsData]);

  const handleTableChange = useCallback((_, __, sorter) => {
    if (!sorter || !sorter.columnKey) {
      setSortConfig({ columnKey: null, order: null });
      return;
    }
    if (!sorter.order) {
      setSortConfig({ columnKey: null, order: null });
      return;
    }
    const order = sorter.order === "ascend" ? "asc" : "desc";
    setSortConfig((prev) => {
      if (prev.columnKey === sorter.columnKey && prev.order === order) {
        return prev;
      }
      return { columnKey: sorter.columnKey, order };
    });
  }, []);

  const getColumnSortOrder = (key) => {
    if (sortConfig.columnKey !== key) {
      return undefined;
    }
    return sortConfig.order === "asc" ? "ascend" : "descend";
  };

  const updateRecord = (response) => {
    const updateRecordData = (records) =>
      records.map((rec) => {
        return rec.id === response.id
          ? {
            ...rec,
            status: response.status,
            committed_amount: response.committed_amount,
            hot_lead: response.hot_lead,
          }
          : rec;
      });
    setData((prevData) => updateRecordData(prevData));
    setPageData((prevData) => updateRecordData(prevData));
  };

  const startupStatusChanged = async (investor, action, amount = null) => {
    dispatch({ type: "toggleLoad" });
    const response = await Api.changeStartupStatus(
      startupID,
      investor.id,
      action,
      null,
      amount
    );
    if (response.status === 200) {
      updateRecord(response.data);
      dispatch({ type: "toggleLoad" });
      setshowAmountPopup(false);
    }
  };

  const hotLeadChanged = async (investor, hotLeadValue) => {
    dispatch({ type: "toggleLoad" });
    const response = await Api.changeStartupStatus(
      startupID,
      investor.id,
      null,
      null,
      null,
      hotLeadValue
    );
    if (response.status === 200) {
      updateRecord(response.data);
      dispatch({ type: "toggleLoad" });
    }
  };

  const extractInvestors = (data) => {
    let investors = [];
    (data || []).forEach((record) => {
      investors.push(record.investor);
    });
    return investors;
  };

  const back = () => {
    navigate('/');
  };

  const csvHeaders = [
    {
      label: 'Name',
      key: 'investor.username'
    },
    {
      label: 'Fund name',
      key: 'investor.fund_name'
    },
    {
      label: 'Whose contact',
      key: 'investor.contact_person'
    },
    {
      label: 'Relationship Manager',
      key: 'investor.relationship_manager'
    },
    {
      label: 'Last Chat Description',
      key: 'chat_description'
    },
    {
      label: 'Status',
      key: 'status'
    },
    {
      label: 'Hot Lead',
      key: 'hot_lead'
    },
    {
      label: 'Assigned',
      key: 'created'
    },
  ]

  const csvData = useMemo(() => {
    return pageData.map(item => ({
      ...item,
      status: startupStatusMap[item.status] || item.status,
      created: item.created ? dayjs(item.created).format("MMM DD, YYYY HH:mm A") : '',
    }));
  }, [pageData]);

  const headers = [
    {
      title: 'Name',
      key: 'name',
      dataIndex: 'investor',
      sorter: true,
      sortOrder: getColumnSortOrder('name'),
      render: (record) => {
        return <div>{`${record.username}`}</div>
      },
    },
    {
      title: 'Fund name',
      key: 'fund_name',
      dataIndex: 'investor',
      sorter: true,
      sortOrder: getColumnSortOrder('fund_name'),
      render: (record) => {
        return <div>{`${record.fund_name}`}</div>
      },
    },
    {
      title: 'Whose contact',
      key: 'whose_contact',
      dataIndex: 'investor',
      sorter: true,
      sortOrder: getColumnSortOrder('whose_contact'),
      render: (record) => {
        return <div>{`${record.contact_person}`}</div>
      },
    },
    {
      title: 'Relationship Manager',
      key: 'relationship_manager',
      dataIndex: 'investor',
      sorter: true,
      sortOrder: getColumnSortOrder('relationship_manager'),
      render: (record) => {
        return <div>{`${record.relationship_manager}`}</div>
      },
    },
    {
      title: 'Last Chat Description',
      dataIndex: 'chat_description',
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      sorter: true,
      sortOrder: getColumnSortOrder('status'),
      width: 180,
      render: (record, rowData) => {
        return <div>
          <Select
            options={startupStatusDataSource}
            style={{ width: "100%" }}
            value={startupStatusMap[record]}
            onChange={(e) => {
              if (e === "Committed") {
                setCommittingInvestor(rowData.investor);
                setshowAmountPopup(true);
              } else {
                startupStatusChanged(
                  rowData.investor,
                  reverseStartupStatusMap[e],
                  null
                );
              }
            }}
          />
          {rowData.status === "Committed" && (
            <p className="mt-1 font-italic">
              with ₹{rowData.committed_amount}
            </p>
          )}
        </div>
      },
    },
    {
      title: 'Hot Lead',
      key: 'hot_lead',
      dataIndex: 'hot_lead',
      render: (record, rowData) => {
        return <Checkbox
          checked={!!record}
          onChange={(e) => hotLeadChanged(rowData.investor, e.target.checked)}
        />
      },
    },
    {
      title: 'Assigned',
      key: 'assigned',
      dataIndex: 'created',
      sorter: true,
      sortOrder: getColumnSortOrder('assigned'),
      render: (record) => {
        return <div>{`${dayjs(record).format("MMM DD, YYYY HH:mm A")}`}</div>
      },
    },
  ]

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex" style={{ justifyContent: "space-between" }}>
          <img
            src="/logos/back.png"
            alt="back"
            className="pointer h-25"
            onClick={back}
          />

          <h3 className="d-inline mt-0 ml-2 text-capitalize">{startupName}</h3>
          <div>
            {!!pageData.length && <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={`${startupName}_match_records.csv`}
            >
              Export to CSV
            </CSVLink>}
          </div>


        </div>
        <AntTable
          data={data}
          headers={headers}
          options={{
            loading,
            pagination: {
              current: currentPage,
              total: totalRecords,
              pageSize: pageSize,
              showSizeChanger: false,
              onChange: (page) => {
                getInvestorsData(startupID, page, searchTerm, sortParam, selectedTag, hotLeadFilter);
              },
            },
            search: {
              enabled: true,
              apiEnabled: true,
              columns: [
                "investor.username",
                "investor.fund_name",
                "chat_description",
                "investor.relationship_manager",
              ],
              onSearch: (term) => {
                handleSearch(term || "");
              },
              onClear: () => {
                handleSearchClear();
              },
              searchExtra: (
                <>
                  <span style={{ whiteSpace: 'nowrap', alignSelf: 'center', marginRight: 4 }}>Hot Leads:</span>
                  <Checkbox
                    style={{ alignSelf: 'center', marginRight: 8 }}
                    checked={hotLeadFilter}
                    onChange={(e) => setHotLeadFilter(e.target.checked)}
                  />
                  <Select
                    style={{ width: "12%" }}
                    allowClear
                    placeholder="Tags"
                    value={selectedTag}
                    options={Utils.convertToAntSelectDatasource(formFields?.tag_choices || [])}
                    onChange={(value) => {
                      setSelectedTag(value);
                    }}
                  />
                </>
              ),
            },
            onChange: handleTableChange,
          }}
        />
      </div>
      <div className="col-lg-12" style={{ marginBottom: 10 }}>
        <DocumentBox startupId={startupID} />
        <CommWidget startupID={startupID} investors={extractInvestors(pageData)} />
      </div>
      <AddAmountForm
        visible={showAmountPopup}
        onClose={() => setshowAmountPopup(false)}
        onSubmit={(payload) => {
          startupStatusChanged(
            committingInvestor,
            "Committed",
            payload.committedAmount
          );
        }}
      />
    </div>
  );
}
